import { useState, useMemo, useEffect } from 'react';
import { formatDisplayName } from '../../utils/nameUtils';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Button, Badge, Alert, Modal } from 'flowbite-react';
import {
  HiCalendar,
  HiGlobe,
  HiLocationMarker,
  HiMail,
  HiPhone,
  HiPlus,
  HiPencil,
  HiTrash,
  HiRefresh,
  HiUserGroup,
  HiScale
} from 'react-icons/hi';
import { DataTable, type ColumnDef } from '../tables';
import { EmptyState } from '../common/EmptyState';
import type { LEAResponse } from '../../types/api';
import { CreateStaffModal } from '../modals/CreateStaffModal';
import { ManageContactStatusModal } from '../modals/ManageContactStatusModal';
import { AddBoardMemberModal } from '../modals/AddBoardMemberModal';
import { AssignmentsManager } from '../staff/AssignmentsManager';
import { staffApi, personsApi, leasApi, getUserErrorMessage } from '../../api';
import { descriptorsApi } from '../../api/descriptors';

interface BoardTabProps {
  lea: LEAResponse;
  onUpdate: () => void;
  canEdit?: boolean;
  canViewAddresses?: boolean;
}

interface BoardMember {
  assignmentId: number;
  staffId: number;
  personId: number;
  fullName: string;
  email?: string;
  phone?: string;
  positionTitle: string | null;
  endDate?: string | null;
  address?: string | null;
}

// Check if a board member's assignment has ended
const isContactInactive = (endDate?: string | null): boolean => {
  if (!endDate) return false;
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return end <= today;
};

export const BoardTab: React.FC<BoardTabProps> = ({ lea, onUpdate, canEdit = false, canViewAddresses = false }) => {
  const navigate = useNavigate();
  const [showCreateBoardMemberModal, setShowCreateBoardMemberModal] = useState(false);
  const [boardAddresses, setBoardAddresses] = useState<Record<number, string>>({});
  const [showCreateSolicitorModal, setShowCreateSolicitorModal] = useState(false);
  const [showManageAssignmentsModal, setShowManageAssignmentsModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<{ staffId: number; personId: number; personName: string; assignments: any[] } | null>(null);
  const [_assignmentType, setAssignmentType] = useState<'board' | 'solicitor'>('board');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [boardMemberDescriptorId, setBoardMemberDescriptorId] = useState<number | undefined>();
  const [showManageStatusModal, setShowManageStatusModal] = useState(false);
  const [selectedPersonForStatus, setSelectedPersonForStatus] = useState<{ personId: number; staffId: number; name: string } | null>(null);
  const [showAddBoardMemberModal, setShowAddBoardMemberModal] = useState(false);
  const [newBoardMemberStaffId, setNewBoardMemberStaffId] = useState<number | null>(null);

  // Meeting schedule and board webpage editing state
  const [editingMeetingSchedule, setEditingMeetingSchedule] = useState(false);
  const [meetingScheduleValue, setMeetingScheduleValue] = useState('');
  const [editingBoardWebpage, setEditingBoardWebpage] = useState(false);
  const [boardWebpageValue, setBoardWebpageValue] = useState('');
  const [savingMeetingInfo, setSavingMeetingInfo] = useState(false);

  // Load SchoolBoardMember descriptor ID for display purposes
  useEffect(() => {
    const loadBoardMemberDescriptor = async () => {
      try {
        const roles = await descriptorsApi.getByTypeName('Staff Role');

        // Find SchoolBoardMember descriptor ID (used for reference)
        const boardMemberDescriptor = roles.find(
          r => r.codeValue === 'SchoolBoardMember' || r.codeValue === 'BoardMember'
        );
        setBoardMemberDescriptorId(boardMemberDescriptor?.descriptorId);
      } catch (err) {
        console.error('Failed to load SchoolBoardMember descriptor:', err);
      }
    };

    loadBoardMemberDescriptor();
  }, []);

  // Load addresses for board members (admin only)
  useEffect(() => {
    if (!canViewAddresses || !lea.administrators) return;

    const boardPersonIds = new Set<number>();
    for (const s of lea.administrators) {
      const role = s.role?.toLowerCase() || '';
      const title = s.positionTitle?.toLowerCase() || '';
      if (role.startsWith('board') || title.includes('board') || title.includes('solicitor')) {
        boardPersonIds.add(s.personId);
      }
    }

    if (boardPersonIds.size === 0) return;

    const loadAddresses = async () => {
      const addressMap: Record<number, string> = {};
      await Promise.all(
        Array.from(boardPersonIds).map(async (personId) => {
          try {
            const addresses = await personsApi.getAddresses(personId);
            const current = addresses.find(a => a.isCurrent) || addresses[0];
            if (current) {
              const parts = [current.streetNumberName];
              if (current.city) parts.push(current.city);
              if (current.stateAbbreviation) parts.push(current.stateAbbreviation);
              if (current.postalCode) parts.push(current.postalCode);
              addressMap[personId] = parts.filter(Boolean).join(', ');
            }
          } catch {
            // Silently skip - user may not have access to this person's addresses
          }
        })
      );
      setBoardAddresses(addressMap);
    };

    loadAddresses();
  }, [canViewAddresses, lea.administrators]);

  const handleStaffCreated = async (staffId: number, _personId: number, type: 'board' | 'solicitor') => {
    // Staff created - close the creation modal
    if (type === 'board') {
      setShowCreateBoardMemberModal(false);
      // For board members, open the specialized AddBoardMemberModal
      setNewBoardMemberStaffId(staffId);
      setShowAddBoardMemberModal(true);
    } else {
      setShowCreateSolicitorModal(false);
      // For solicitors, continue with the general assignments flow
      setAssignmentType(type);

      try {
        const fullStaff = await staffApi.get(staffId);

        setSelectedStaff({
          staffId: fullStaff.staffId,
          personId: fullStaff.person.personId,
          personName: formatDisplayName({ ...fullStaff.person, lastName: fullStaff.person.lastSurname }),
          assignments: fullStaff.assignments
        });

        setShowManageAssignmentsModal(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load staff member');
      }
    }
  };

  const handleBoardMemberAssignmentSuccess = () => {
    // Assignment created successfully - refresh the data
    setShowAddBoardMemberModal(false);
    setNewBoardMemberStaffId(null);
    setSuccessMessage('Board member added successfully');
    onUpdate();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Meeting schedule handlers
  const handleEditMeetingSchedule = () => {
    setMeetingScheduleValue(lea.boardMeetingSchedule || '');
    setEditingMeetingSchedule(true);
  };

  const handleSaveMeetingSchedule = async () => {
    setSavingMeetingInfo(true);
    try {
      await leasApi.update(lea.leaId, { boardMeetingSchedule: meetingScheduleValue || null });
      setEditingMeetingSchedule(false);
      onUpdate();
    } catch (err: any) {
      setError(getUserErrorMessage(err, 'Failed to update meeting schedule'));
    } finally {
      setSavingMeetingInfo(false);
    }
  };

  // Board webpage handlers
  const handleEditBoardWebpage = () => {
    setBoardWebpageValue(lea.boardWebpage || '');
    setEditingBoardWebpage(true);
  };

  const handleSaveBoardWebpage = async () => {
    setSavingMeetingInfo(true);
    try {
      await leasApi.update(lea.leaId, { boardWebpage: boardWebpageValue || null });
      setEditingBoardWebpage(false);
      onUpdate();
    } catch (err: any) {
      setError(getUserErrorMessage(err, 'Failed to update board webpage'));
    } finally {
      setSavingMeetingInfo(false);
    }
  };

  const handleUpdateAssignments = async () => {
    // Refresh the organization data to show updated assignments
    onUpdate();

    // Refresh the selected staff member's assignments
    if (selectedStaff) {
      try {
        const updatedStaff = await staffApi.get(selectedStaff.staffId);
        setSelectedStaff({
          ...selectedStaff,
          assignments: updatedStaff.assignments
        });
      } catch (err) {
        console.error('Failed to refresh staff assignments:', err);
      }
    }
  };

  // Handle editing a board member's assignments
  const handleEditBoardMember = async (boardMember: BoardMember) => {
    try {
      const fullStaff = await staffApi.get(boardMember.staffId);
      setSelectedStaff({
        staffId: fullStaff.staffId,
        personId: boardMember.personId,
        personName: boardMember.fullName,
        assignments: fullStaff.assignments
      });
      setShowManageAssignmentsModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load staff member');
    }
  };

  // Helper to map administrator to BoardMember
  const mapToBoardMember = (s: typeof lea.administrators[0]): BoardMember => ({
    assignmentId: s.staffEdOrgAssignmentAssociationId,
    staffId: s.staffId,
    personId: s.personId,
    fullName: s.displayName || formatDisplayName(s),
    email: s.email || undefined,
    phone: s.phone || undefined,
    positionTitle: s.positionTitle,
    endDate: s.endDate,
    address: boardAddresses[s.personId] || null
  });

  // Helper to check if a record is board-related
  // Matches the StaffTab exclusion logic: role starts with "board" OR title includes "board"
  const isBoardRelated = (s: typeof lea.administrators[0]): boolean => {
    const role = s.role?.toLowerCase() || '';
    const title = s.positionTitle?.toLowerCase() || '';
    return role.startsWith('board') || title.includes('board');
  };

  // Helper to check if someone has a BoardMember role (for voting determination)
  const hasBoardMemberRole = (s: typeof lea.administrators[0]): boolean => {
    const role = s.role?.toLowerCase() || '';
    return role === 'boardmember';
  };

  // Extract VOTING board members
  // Voting = has BoardMember role OR has board role AND a BoardMember SEOAA elsewhere
  // For simplicity: anyone with role === 'boardmember' is voting
  // Plus anyone with role.startsWith('board') who ALSO has a BoardMember SEOAA
  const votingBoardMembers: BoardMember[] = useMemo(() => {
    if (!lea.administrators) return [];

    // First, find all people who have a BoardMember role
    const peopleWithBoardMemberRole = new Set<number>();
    for (const s of lea.administrators) {
      if (hasBoardMemberRole(s)) {
        peopleWithBoardMemberRole.add(s.personId);
      }
    }

    // Get all board-related records for voting members
    const boardRecords = lea.administrators.filter(s => {
      const role = s.role?.toLowerCase() || '';
      // Include if: has board role AND person has BoardMember SEOAA somewhere
      return role.startsWith('board') && peopleWithBoardMemberRole.has(s.personId);
    });

    // Group by personId and pick the best record for each person
    const personMap = new Map<number, typeof boardRecords[0]>();
    for (const record of boardRecords) {
      const existing = personMap.get(record.personId);
      if (!existing) {
        personMap.set(record.personId, record);
      } else {
        // First priority: prefer ACTIVE records over INACTIVE ones
        const existingIsInactive = isContactInactive(existing.endDate);
        const newIsInactive = isContactInactive(record.endDate);

        if (existingIsInactive && !newIsInactive) {
          // New record is active, existing is inactive - prefer new
          personMap.set(record.personId, record);
        } else if (!existingIsInactive && newIsInactive) {
          // Existing is active, new is inactive - keep existing
          // Do nothing
        } else {
          // Both same active status - use other criteria
          // Prefer record with positionTitle
          const existingHasTitle = !!existing.positionTitle;
          const newHasTitle = !!record.positionTitle;
          // Prefer specific role over generic "boardmember"
          const existingIsBoardMember = hasBoardMemberRole(existing);
          const newIsBoardMember = hasBoardMemberRole(record);

          if ((!existingHasTitle && newHasTitle) ||
              (existingIsBoardMember && !newIsBoardMember)) {
            personMap.set(record.personId, record);
          }
        }
      }
    }

    return Array.from(personMap.values()).map(mapToBoardMember);
  }, [lea.administrators, boardAddresses]);

  // Extract NON-VOTING board roles
  // Non-voting = board-related (by role OR title) but NO BoardMember SEOAA
  // This catches:
  // - People with board roles (e.g., boardSecretary) but no BoardMember tag
  // - People with "board" in title but no board role (title-based fallback)
  const nonVotingBoardRoles: BoardMember[] = useMemo(() => {
    if (!lea.administrators) return [];

    // Find all people who have a BoardMember role
    const peopleWithBoardMemberRole = new Set<number>();
    for (const s of lea.administrators) {
      if (hasBoardMemberRole(s)) {
        peopleWithBoardMemberRole.add(s.personId);
      }
    }

    // Get all board-related records for people WITHOUT BoardMember role
    const nonVotingRecords = lea.administrators.filter(s => {
      return isBoardRelated(s) && !peopleWithBoardMemberRole.has(s.personId);
    });

    // Group by personId and pick the best record for each person
    const personMap = new Map<number, typeof nonVotingRecords[0]>();
    for (const record of nonVotingRecords) {
      const existing = personMap.get(record.personId);
      if (!existing) {
        personMap.set(record.personId, record);
      } else {
        // First priority: prefer ACTIVE records over INACTIVE ones
        const existingIsInactive = isContactInactive(existing.endDate);
        const newIsInactive = isContactInactive(record.endDate);

        if (existingIsInactive && !newIsInactive) {
          // New record is active, existing is inactive - prefer new
          personMap.set(record.personId, record);
        } else if (!existingIsInactive && newIsInactive) {
          // Existing is active, new is inactive - keep existing
          // Do nothing
        } else if (!existing.positionTitle && record.positionTitle) {
          // Both same active status - prefer one with positionTitle
          personMap.set(record.personId, record);
        }
      }
    }

    return Array.from(personMap.values()).map(mapToBoardMember);
  }, [lea.administrators, boardAddresses]);

  // Combined for backward compatibility (e.g., count display)
  const allBoardMembers = useMemo(() =>
    [...votingBoardMembers, ...nonVotingBoardRoles],
    [votingBoardMembers, nonVotingBoardRoles]
  );

  // Extract solicitors from administrators based on position title
  const solicitors = useMemo(() => {
    if (!lea.administrators) return [];

    return lea.administrators
      .filter(s => {
        const title = s.positionTitle?.toLowerCase() || '';
        return title.includes('solicitor');
      })
      .map(s => ({
        assignmentId: s.staffEdOrgAssignmentAssociationId,
        staffId: s.staffId,
        personId: s.personId,
        fullName: s.displayName || formatDisplayName(s),
        email: s.email || undefined,
        phone: s.phone || undefined,
        firmName: s.positionTitle
      }));
  }, [lea.administrators]);

  const boardColumns: ColumnDef<BoardMember>[] = useMemo(() => [
    {
      id: 'fullName',
      header: 'Board Member',
      accessor: 'fullName',
      sortable: true,
      searchable: true,
      render: (_value, row) => (
        <Link
          to={`/persons/${row.personId}`}
          className="text-left hover:underline block"
        >
          <div className="flex items-center gap-2">
            <span className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">{row.fullName}</span>
            {isContactInactive(row.endDate) && (
              <Badge color="gray" size="xs">Inactive</Badge>
            )}
          </div>
          {row.positionTitle && (
            <Badge color="info" size="xs" className="mt-1">
              {row.positionTitle}
            </Badge>
          )}
        </Link>
      )
    },
    {
      id: 'contact',
      header: 'Contact',
      accessor: 'email',
      sortable: true,
      searchable: true,
      render: (_value, row) => (
        <div className="space-y-1">
          {row.email && (
            <a
              href={`mailto:${row.email}`}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <HiMail className="mr-1 h-3 w-3" />
              {row.email}
            </a>
          )}
          {row.phone && (
            <a
              href={`tel:${row.phone}`}
              className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              onClick={(e) => e.stopPropagation()}
            >
              <HiPhone className="mr-1 h-3 w-3" />
              {row.phone}
            </a>
          )}
        </div>
      )
    },
    // Address column (admin only)
    ...(canViewAddresses ? [{
      id: 'address',
      header: 'Address',
      accessor: 'address' as keyof BoardMember,
      sortable: true,
      searchable: true,
      render: (_value: any, row: BoardMember) => row.address ? (
        <div className="flex items-start text-sm text-gray-600 dark:text-gray-400">
          <HiLocationMarker className="mr-1 h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500" />
          <span>{row.address}</span>
        </div>
      ) : (
        <span className="text-gray-400 dark:text-gray-500 text-sm italic">—</span>
      )
    }] : []),
    // Only include actions column if user can edit
    ...(canEdit ? [{
      id: 'actions',
      header: 'Actions',
      accessor: 'assignmentId' as keyof BoardMember,
      sortable: false,
      searchable: false,
      width: '100px',
      align: 'center' as const,
      render: (_value: any, row: BoardMember) => (
        <div className="flex justify-center gap-2">
          <Button
            size="xs"
            color="light"
            onClick={(e) => {
              e.stopPropagation();
              handleEditBoardMember(row);
            }}
            className="text-gray-600 hover:text-gray-700"
            aria-label={`Edit ${row.fullName}`}
          >
            <HiPencil className="h-4 w-4" />
          </Button>
          <Button
            size="xs"
            color="light"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedPersonForStatus({
                personId: row.personId,
                staffId: row.staffId,
                name: row.fullName
              });
              setShowManageStatusModal(true);
            }}
            className={isContactInactive(row.endDate)
              ? "text-blue-600 hover:text-blue-700"
              : "text-red-600 hover:text-red-700"}
            aria-label={isContactInactive(row.endDate) ? `Reactivate ${row.fullName}` : `Manage ${row.fullName}`}
          >
            {isContactInactive(row.endDate) ? (
              <HiRefresh className="h-4 w-4" />
            ) : (
              <HiTrash className="h-4 w-4" />
            )}
          </Button>
        </div>
      )
    }] : [])
  ], [canEdit, canViewAddresses, handleEditBoardMember]);

  return (
    <div className="p-6 space-y-6">
      {error && (
        <Alert color="failure" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert color="success" onDismiss={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Board Meeting Information */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <HiCalendar className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
          Board Meeting Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900 dark:text-white">Meeting Schedule</h4>
              {canEdit && !editingMeetingSchedule && (
                <button
                  onClick={handleEditMeetingSchedule}
                  className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Edit meeting schedule"
                >
                  <HiPencil className="h-4 w-4" />
                </button>
              )}
            </div>
            {editingMeetingSchedule ? (
              <div className="space-y-2">
                <textarea
                  value={meetingScheduleValue}
                  onChange={(e) => setMeetingScheduleValue(e.target.value)}
                  placeholder="e.g., 2nd Tuesday monthly at 7:00 PM"
                  rows={3}
                  className="w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveMeetingSchedule}
                    disabled={savingMeetingInfo}
                    className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                  >
                    {savingMeetingInfo ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingMeetingSchedule(false)}
                    disabled={savingMeetingInfo}
                    className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : lea.boardMeetingSchedule ? (
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{lea.boardMeetingSchedule}</p>
            ) : (
              <p className="text-gray-400 dark:text-gray-500 italic">No meeting schedule available</p>
            )}
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-medium text-gray-900 dark:text-white">Board Website</h4>
              {canEdit && !editingBoardWebpage && (
                <button
                  onClick={handleEditBoardWebpage}
                  className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Edit board website"
                >
                  <HiPencil className="h-4 w-4" />
                </button>
              )}
            </div>
            {editingBoardWebpage ? (
              <div className="space-y-2">
                <input
                  type="url"
                  value={boardWebpageValue}
                  onChange={(e) => setBoardWebpageValue(e.target.value)}
                  placeholder="https://www.example.org/board"
                  className="w-full text-sm border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveBoardWebpage}
                    disabled={savingMeetingInfo}
                    className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                  >
                    {savingMeetingInfo ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingBoardWebpage(false)}
                    disabled={savingMeetingInfo}
                    className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : lea.boardWebpage ? (
              <a
                href={lea.boardWebpage.startsWith('http') ? lea.boardWebpage : `https://${lea.boardWebpage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                <HiGlobe className="mr-2 h-4 w-4" />
                Visit Board Website
              </a>
            ) : (
              <p className="text-gray-400 dark:text-gray-500 italic">No board website available</p>
            )}
          </Card>
        </div>
      </div>

      {/* Board Members (Voting) */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <HiUserGroup className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
            Board Members ({votingBoardMembers.length})
          </h3>
          {canEdit && (
            <Button
              size="sm"
              color="light"
              onClick={() => setShowCreateBoardMemberModal(true)}
              className="text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              <HiPlus className="mr-2 h-4 w-4" />
              Add Board Member
            </Button>
          )}
        </div>

        {votingBoardMembers.length > 0 ? (
          <DataTable
            data={votingBoardMembers}
            columns={boardColumns}
            searchable={false}
            sortable={true}
            pagination={false}
            selectable={false}
            hoverable={true}
            striped={false}
            emptyMessage="No board members found."
            ariaLabel="Board members table"
          />
        ) : (
          <Card>
            <EmptyState
              title="No board members"
              description="No board members have been assigned to this organization."
              icon={<HiUserGroup className="w-16 h-16 text-gray-400" />}
            />
          </Card>
        )}
      </div>

      {/* Non-Voting Board Roles */}
      {nonVotingBoardRoles.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
            <HiUserGroup className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
            Non-Voting Board Roles ({nonVotingBoardRoles.length})
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            These individuals serve the school board in administrative or support roles (e.g., Board Secretary) but are not elected voting members.
            {canEdit && (
              <span className="block mt-1 text-gray-400 dark:text-gray-500">
                To add a non-voting board role, create a staff assignment with a board-related role (e.g., Board Secretary) but do NOT assign the "Board Member" role tag.
              </span>
            )}
          </p>
          <DataTable
            data={nonVotingBoardRoles}
            columns={boardColumns}
            searchable={false}
            sortable={true}
            pagination={false}
            selectable={false}
            hoverable={true}
            striped={false}
            emptyMessage="No non-voting board roles found."
            ariaLabel="Non-voting board roles table"
          />
        </div>
      )}

      {/* Solicitors */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <HiScale className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
          Legal Representation
        </h3>

        {solicitors.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {solicitors.map((solicitor) => (
              <Card key={solicitor.assignmentId}>
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">{solicitor.fullName}</h4>
                    {solicitor.firmName && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{solicitor.firmName}</p>
                    )}
                    <div className="mt-3 space-y-1">
                      {solicitor.email && (
                        <a
                          href={`mailto:${solicitor.email}`}
                          className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          <HiMail className="mr-1 h-3 w-3" />
                          {solicitor.email}
                        </a>
                      )}
                      {solicitor.phone && (
                        <a
                          href={`tel:${solicitor.phone}`}
                          className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          <HiPhone className="mr-1 h-3 w-3" />
                          {solicitor.phone}
                        </a>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleEditBoardMember({
                        assignmentId: solicitor.assignmentId,
                        staffId: solicitor.staffId,
                        personId: solicitor.personId,
                        fullName: solicitor.fullName,
                        email: solicitor.email,
                        phone: solicitor.phone,
                        positionTitle: solicitor.firmName || null,
                        endDate: null
                      })}
                      className="text-gray-600 hover:text-gray-700"
                      aria-label={`Edit ${solicitor.fullName}`}
                    >
                      <HiPencil className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <EmptyState
              title="No solicitor assigned"
              description="No legal representation has been assigned to this organization."
              icon={<HiScale className="w-16 h-16 text-gray-400" />}
              action={canEdit ? {
                label: 'Add Solicitor',
                onClick: () => setShowCreateSolicitorModal(true),
                icon: <HiPlus className="mr-2 h-4 w-4" />
              } : undefined}
            />
          </Card>
        )}
      </div>

      {/* Create Board Member Modal */}
      {canEdit && (
        <CreateStaffModal
          isOpen={showCreateBoardMemberModal}
          onClose={() => setShowCreateBoardMemberModal(false)}
          onSuccess={({ staffId, personId }) => handleStaffCreated(staffId, personId, 'board')}
          defaultOrganizationId={lea.educationOrganizationId}
          modalTitle="Add Board Member"
        />
      )}

      {/* Create Solicitor Modal */}
      {canEdit && (
        <CreateStaffModal
          isOpen={showCreateSolicitorModal}
          onClose={() => setShowCreateSolicitorModal(false)}
          onSuccess={({ staffId, personId }) => handleStaffCreated(staffId, personId, 'solicitor')}
          defaultOrganizationId={lea.educationOrganizationId}
          defaultPositionTitle="Solicitor"
          modalTitle="Add Solicitor"
        />
      )}

      {/* Manage Assignments Modal */}
      {selectedStaff && (
        <Modal
          show={showManageAssignmentsModal}
          onClose={() => {
            setShowManageAssignmentsModal(false);
            setSelectedStaff(null);
          }}
          dismissible
          size="3xl"
        >
          <div className="flex flex-col max-h-[85vh]">
            {/* Header - fixed */}
            <div className="flex items-center justify-between p-6 pb-4 border-b flex-shrink-0">
              <h3 className="text-xl font-medium text-gray-900">
                Manage Assignments: {selectedStaff.personName}
              </h3>
              <button
                onClick={() => {
                  setShowManageAssignmentsModal(false);
                  setSelectedStaff(null);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Profile link hint */}
            <div className="px-6 pt-3 pb-0">
              <p className="text-sm text-gray-500">
                <a
                  href={`/persons/${selectedStaff.personId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setShowManageAssignmentsModal(false);
                    setSelectedStaff(null);
                    navigate(`/persons/${selectedStaff.personId}`);
                  }}
                  className="text-blue-600 hover:text-blue-700 hover:underline"
                >
                  Visit {selectedStaff.personName}'s profile
                </a>
                {' '}to edit contact information.
              </p>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto p-6">
              <AssignmentsManager
                staffId={selectedStaff.staffId}
                assignments={selectedStaff.assignments}
                onUpdate={handleUpdateAssignments}
                lockedOrganizationId={lea.educationOrganizationId}
                prioritizeBoardRoles={true}
              />
            </div>

            {/* Footer - fixed */}
            <div className="p-6 pt-4 border-t flex justify-end flex-shrink-0">
              <Button
                color="gray"
                onClick={() => {
                  setShowManageAssignmentsModal(false);
                  setSelectedStaff(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Manage Contact Status Modal */}
      {selectedPersonForStatus && (
        <ManageContactStatusModal
          isOpen={showManageStatusModal}
          onClose={() => {
            setShowManageStatusModal(false);
            setSelectedPersonForStatus(null);
          }}
          personId={selectedPersonForStatus.personId}
          personName={selectedPersonForStatus.name}
          staffId={selectedPersonForStatus.staffId}
          onUpdate={onUpdate}
        />
      )}

      {/* Add Board Member Assignment Modal */}
      {newBoardMemberStaffId && (
        <AddBoardMemberModal
          isOpen={showAddBoardMemberModal}
          onClose={() => {
            setShowAddBoardMemberModal(false);
            setNewBoardMemberStaffId(null);
          }}
          staffId={newBoardMemberStaffId}
          organizationId={lea.educationOrganizationId}
          organizationName={lea.nameOfInstitution}
          onSuccess={handleBoardMemberAssignmentSuccess}
        />
      )}
    </div>
  );
};